import React, { useEffect, useState } from "react";
import {
	Box,
	Paper,
	Text,
	Avatar,
	Button,
	Group,
	Stack,
	Divider,
	Center,
	Card,
	ScrollArea,
	Title,
	Alert
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import md5 from "md5";
import { emailStepStore } from "@/entities/StepEmail/store/emailStepStore";
import { appStore } from "@/app/store/appStore";
import { basePath } from "@/app/AppRoutes";
import {
	FxIdApplicationHandlersAuthAuthenticateWithExternalIdState,
	FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse
} from "@/api/gen";
import { AuthenticateUser } from "@/api/OpenApiClient";
import { IOtherLogin, persistStore } from "@entities/StepFolder/store/persistStore";
import { getAuthToken } from "@/api/Auth";

export const LoginList: React.FC = () => {
	const { t } = useTranslation();
	const appCode = appStore((state) => state.appCode);
	const otherLogins = persistStore((state) => state.otherLogins);
	const authToken = appStore((state) => state.authToken);
	const oauthRedirectUrl = appStore((state) => state.oauthRedirectUrl);
	const responseData = emailStepStore((state) => state.responseData);
	const [error, setError] = useState<string | null>(null);

 const openOauthRedirect = (gameAccessToken: string) => {
     const baseUrl = appStore.getState().oauthRedirectUrl || "";
     console.log(`onClose Received response: ${baseUrl}`);
 
     let finalUrl: string;
     if (baseUrl.includes("{token_value}")) {
         finalUrl = baseUrl.replace("{token_value}", gameAccessToken);
     } else {
         const oauthUrl = new URL(baseUrl);
         oauthUrl.searchParams.append("token", gameAccessToken);
         finalUrl = oauthUrl.toString();
     }
 
     const stateParam = appStore.getState().oauthState;
     if (stateParam) {
         const urlObj = new URL(finalUrl);
         urlObj.searchParams.append("state", stateParam);
         finalUrl = urlObj.toString();
     }
 
     console.log(`onClose Redirecting to: ${finalUrl}`);
     window.location.href = finalUrl;
 };

	const onLoginComplete = (res: FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse, login: IOtherLogin) => {
		console.log(`onClose Received response: ${JSON.stringify(res)}`);
		const token = res.Tokens?.GameAccessToken;
		if (
			token &&
			res.State === FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.Success &&
			res.Tokens?.GameAccessToken
		) {
			console.log(`��� записываю AccessToken для openOauthRedirect ${res.Email}`);
			openOauthRedirect(res.Tokens?.GameAccessToken);
		} else {
			emailStepStore.getState().setIsLoading(false);
			setError(
				t("common.LoginOutdated", "Login is {{login}} outdated and must be logged in again.", {
					login: login.username
				})
			);
		}
	};

	const handleOauthLogin = async (login: IOtherLogin) => {
		if (!oauthRedirectUrl) {
			emailStepStore.getState().startRequest(login.refreshToken);
			return;
		}

		try {
			emailStepStore.getState().setIsLoading(true);
			const request = {
				GameSystemName: appCode,
				ExternalSocialIdentifier: null,
				RefreshToken: login.refreshToken
			};
			const newResponse = await AuthenticateUser(request, appCode);
			onLoginComplete(newResponse, login);
		} catch (error) {
			console.log(error);
			emailStepStore.getState().setIsLoading(false);
		}
	};

	const handleNewLogin = () => {
		emailStepStore.getState().saveCurrentLogin();
		emailStepStore.getState().logout();
		appStore.getState().setLoginFlow(true);
	};

	const [allLogins, setAllLogins] = useState<IOtherLogin[]>([]);

	useEffect(() => {
		const loginsThisApp = otherLogins.filter((login) => login.appCode === appCode);
		if(emailStepStore.getState().login && persistStore.getState().refreshToken) {
			const currentLogin: IOtherLogin = {
				username: emailStepStore.getState().login ||"",
				refreshToken: persistStore.getState().refreshToken[appCode]||"",
				appCode: appCode,
				lastActive: new Date()
			};
			if(!loginsThisApp.some((login) => login.username === currentLogin.username)) {
				loginsThisApp.push(currentLogin);
			}
		}
		setAllLogins(
			loginsThisApp
		);
		if (loginsThisApp.length == 0) {

			if (authToken) {
				const gameAccessToken = responseData?.Tokens?.GameAccessToken;
				if (gameAccessToken)
				{
					openOauthRedirect(gameAccessToken);
					return;
				}
			}

			appStore.getState().setLoginFlow(true);
		}
	}, [appCode,authToken, otherLogins,responseData]);

	const getGravatarUrl = (email: string | null) => {
		if (!email) return `${basePath}${appCode}/avatar-icon.png`;
		return `https://www.gravatar.com/avatar/${md5(email.toLowerCase())}?d=${encodeURIComponent(
			`${basePath}${appCode}/avatar-icon.png`
		)}`;
	};

	//if (!oauthRedirectUrl) return;

	return (
		<Center p={"md"} >
			<Stack  w={400}>
				<Title size="xl">
					{t("common.ChooseAccount", "Choose an account")}
				</Title>

				{error && (
					<Alert color={"red"} withCloseButton title={t("common.LoginFailed", "Login failed")}>
						{error}
					</Alert>
				)}
				<ScrollArea scrollbars={"y"} h={200}>
					{allLogins.map((login, index) => (
						<Box
							key={`${login.username}-${login.appCode}-${index}`}
							className={"hoverBox"}
							onClick={() => handleOauthLogin(login as IOtherLogin)}
						>
							<Group>
								<Avatar src={getGravatarUrl(login.username)} radius="xl" size="md" />
								<Box style={{ flex: 1 }}>
									<Text size="sm" w={200}>
										{login.username}
									</Text>
								</Box>
							</Group>
						</Box>
					))}
				</ScrollArea>

				<Divider />
				{oauthRedirectUrl && (
					<Text size="xs" c="dimmed">
						{t(
							"common.PrivacyReminder",
							"To continue we will share email address and game id with {{domain}}",
							{
								domain: new URL(oauthRedirectUrl).hostname
							}
						)}
					</Text>
				)}
				<Button
					leftSection={<IconPlus size="1rem" />}
					variant="light"
					color="blue"
					fullWidth
					onClick={handleNewLogin}
				>
					{t("common.AddAnotherAccount", "Add another account")}
				</Button>
			</Stack>
		</Center>
	);
};
export default LoginList;
